/**
 * OrderHealthScore — compact "at-a-glance" health indicator above the
 * Needs Attention grid. See `computeBreakdown` for scoring details.
 *
 * V1 formula:
 *   score = max(0, 100 - weighted_penalty)
 *
 * Bands:
 *   90–100 Excellent  · 75–89 Good · 50–74 Needs Attention · 0–49 Critical
 *
 * Colours live on the score + status text; the card chrome stays neutral
 * so the widget sits comfortably next to Today's Progress.
 */
import React, { useMemo } from 'react';
import { HeartPulse, Info } from 'lucide-react';
import {
    HoverCard,
    HoverCardTrigger,
    HoverCardContent,
} from './ui/hover-card';

const BAND = [
    { min: 90, label: 'Excellent',       ring: '#10b981', text: 'text-emerald-700' },
    { min: 75, label: 'Good',            ring: '#84cc16', text: 'text-lime-700' },
    { min: 50, label: 'Needs Attention', ring: '#f59e0b', text: 'text-amber-700' },
    { min: 0,  label: 'Critical',        ring: '#ef4444', text: 'text-rose-700' },
];

const bandFor = (score) => BAND.find((b) => score >= b.min) || BAND[BAND.length - 1];

const WEIGHTS = {
    internal: 5,
    admin: 3,
    follow_up: 2,
    waiting_customer: 0.5,
};

const computeBreakdown = (summary) => {
    const orders = summary?.orders || {};
    const finance = summary?.finance || {};
    const users = summary?.users || {};

    const internal =
        (orders.supplier_confirmation_needs_verification || 0) +
        (orders.dispatch_needs_verification || 0) +
        (orders.supplier_errors || 0) +
        (orders.ready_for_production || 0) +
        (orders.supplier_validation_mismatch || 0) +
        (orders.master_ready_not_sent || 0) +
        (orders.md5_issues || 0);

    const waiting_customer =
        (orders.missing_artwork || 0) +
        (orders.missing_master || 0) +
        (orders.awaiting_customer_approval || 0);

    const follow_up =
        (orders.ready_to_invoice || 0) +
        (orders.low_margin || 0) +
        (finance.overdue_invoices || 0);

    const admin = users.pending_approvals || 0;

    return { internal, waiting_customer, follow_up, admin };
};

const OrderHealthScore = ({ actionsSummary }) => {
    const { score, band, breakdown, totalPenalty } = useMemo(() => {
        const b = computeBreakdown(actionsSummary);
        const p =
            b.internal * WEIGHTS.internal +
            b.admin * WEIGHTS.admin +
            b.follow_up * WEIGHTS.follow_up +
            b.waiting_customer * WEIGHTS.waiting_customer;
        const s = Math.max(0, Math.min(100, Math.round(100 - p)));
        return { score: s, band: bandFor(s), breakdown: b, totalPenalty: p };
    }, [actionsSummary]);

    const dialStyle = {
        background: `conic-gradient(${band.ring} ${score * 3.6}deg, #e2e8f0 0)`,
    };

    const rows = [
        { key: 'internal',         label: 'Internal action (own)', value: breakdown.internal,         weight: WEIGHTS.internal },
        { key: 'admin',            label: 'Admin / account tasks', value: breakdown.admin,            weight: WEIGHTS.admin },
        { key: 'follow_up',        label: 'Follow-up',             value: breakdown.follow_up,        weight: WEIGHTS.follow_up },
        { key: 'waiting_customer', label: 'Waiting for customer',  value: breakdown.waiting_customer, weight: WEIGHTS.waiting_customer },
    ];

    return (
        <div
            className="rounded-lg border border-slate-200 bg-white px-4 py-2.5 flex items-center gap-3"
            data-testid="order-health-score"
        >
            {/* Compact radial dial */}
            <div className="relative w-11 h-11 flex-shrink-0" data-testid="order-health-score-dial">
                <div className="w-full h-full rounded-full" style={dialStyle} />
                <div className="absolute inset-1 rounded-full bg-white flex items-center justify-center">
                    <span className={`text-sm font-bold tabular-nums ${band.text}`} data-testid="order-health-score-value">
                        {score}
                    </span>
                </div>
            </div>

            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                    <HeartPulse className="h-3 w-3 text-slate-400" />
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 leading-none">
                        Order Health
                    </p>
                    <HoverCard openDelay={100}>
                        <HoverCardTrigger asChild>
                            <button
                                type="button"
                                className="text-slate-400 hover:text-slate-600 transition-colors"
                                aria-label="How is this calculated?"
                                data-testid="order-health-score-info"
                            >
                                <Info className="h-3 w-3" />
                            </button>
                        </HoverCardTrigger>
                        <HoverCardContent align="start" className="w-80 text-xs">
                            <p className="font-semibold text-slate-900 mb-2">Score components</p>
                            <table className="w-full text-[11px]">
                                <thead className="text-slate-500 uppercase text-[10px]">
                                    <tr>
                                        <th className="text-left font-medium py-1">Bucket</th>
                                        <th className="text-right font-medium py-1">Items</th>
                                        <th className="text-right font-medium py-1">Weight</th>
                                        <th className="text-right font-medium py-1">Penalty</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {rows.map((r) => (
                                        <tr key={r.key}>
                                            <td className="py-1 text-slate-700">{r.label}</td>
                                            <td className="py-1 text-right tabular-nums text-slate-900">{r.value}</td>
                                            <td className="py-1 text-right tabular-nums text-slate-500">×{r.weight}</td>
                                            <td className="py-1 text-right tabular-nums text-slate-900">
                                                {(r.value * r.weight).toFixed(1)}
                                            </td>
                                        </tr>
                                    ))}
                                    <tr className="border-t-2 border-slate-200 font-semibold">
                                        <td className="py-1 text-slate-700" colSpan={3}>Total penalty</td>
                                        <td className="py-1 text-right tabular-nums text-slate-900">
                                            {totalPenalty.toFixed(1)}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                            <p className="mt-2 text-slate-500">
                                Score = max(0, 100 − penalty). Future versions will fold in
                                overdue deliveries, supplier performance, and lead-time drift.
                            </p>
                        </HoverCardContent>
                    </HoverCard>
                </div>
                <p className={`text-sm font-semibold ${band.text} leading-tight mt-0.5`} data-testid="order-health-score-label">
                    {band.label}
                </p>
                <p className="text-[11px] text-slate-500 leading-tight">Based on open actions</p>
            </div>
        </div>
    );
};

export default OrderHealthScore;
